import { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { getQrAnalyticsShops } from "../../services";
import { formatNumber, numberWithCommas } from "../../utils";
import {
  QrShopRankRow,
  QrShopSortBy,
  QrShopSortDir,
} from "../../types/qr-analytics.type";
import { DIGITAL_CHANNELS, DIGITAL_GROUP } from "./channel";

/** The two axes this board ranks on — both the เงินโอน+QR group, never QR alone. */
type BoardSortBy = Extract<QrShopSortBy, "digital_count" | "digital_percent">;

interface ShopRankingTableProps {
  readonly title: string;
  /** Caption per sort axis — ranking by volume and by ratio answer different questions. */
  readonly caption: Record<BoardSortBy, string>;
  readonly sortDir: QrShopSortDir;
  readonly dateFrom: string;
  readonly dateTo: string;
  readonly branchCode?: string;
  readonly routeCode?: string;
}

const [TRANSFER_CHANNEL, QR_CHANNEL] = DIGITAL_CHANNELS;

const ROWS_PER_PAGE = 10;

/**
 * Shops ranked by how many เงินโอน+QR bills they generated — the group the owner reads
 * against เงินสด — with each shop's count split into its โอน and QR parts underneath.
 *
 * One component serves both the "เยอะที่สุด" and "น้อยที่สุด" lists, because they are the
 * same query read from opposite ends — a second component would be two chances to disagree
 * about what a rank means. The ascending list deliberately includes shops that paid cash on
 * every bill: a busy shop that has never transferred or scanned is the most useful row on
 * that table, not one to filter out.
 */
export default function ShopRankingTable({
  title,
  caption,
  sortDir,
  dateFrom,
  dateTo,
  branchCode,
  routeCode,
}: ShopRankingTableProps) {
  const [sortBy, setSortBy] = useState<BoardSortBy>("digital_count");
  const [rows, setRows] = useState<QrShopRankRow[]>([]);
  const [count, setCount] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);

  const onGetShops = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data },
      } = await getQrAnalyticsShops({
        date_from: dateFrom,
        date_to: dateTo,
        sort_dir: sortDir,
        sort_by: sortBy,
        page: page + 1,
        limit: ROWS_PER_PAGE,
        branch_code: branchCode,
        route_code: routeCode,
      });
      setRows(data?.rows ?? []);
      setCount(data?.count ?? 0);
    } catch {
      setRows([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, sortDir, sortBy, page, branchCode, routeCode]);

  useEffect(() => {
    onGetShops();
  }, [onGetShops]);

  // A new filter changes what rank 1 means, so the reader must land back on the first page.
  useEffect(() => {
    setPage(0);
  }, [dateFrom, dateTo, branchCode, routeCode, sortBy]);

  return (
    <Paper sx={{ p: 2, height: "100%" }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        gap={1}
      >
        <Box>
          <Typography variant="subtitle2" fontWeight={700}>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {caption[sortBy]}
          </Typography>
        </Box>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={sortBy}
          onChange={(_, next: BoardSortBy | null) => next && setSortBy(next)}
          aria-label="เรียงตาม"
        >
          <ToggleButton value="digital_count">จำนวนบิล</ToggleButton>
          <ToggleButton value="digital_percent">% โอน+QR</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <TableContainer sx={{ mt: 1, overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={40}>#</TableCell>
              <TableCell>ร้านค้า</TableCell>
              <TableCell>สาย</TableCell>
              <TableCell align="right">บิลโอน+QR</TableCell>
              <TableCell align="right">บิลจากแอป</TableCell>
              <TableCell align="right">% โอน+QR</TableCell>
              <TableCell align="right">ยอดโอน+QR</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Box py={3}>
                    <CircularProgress size={24} />
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" py={2}>
                    ไม่มีข้อมูลในช่วงที่เลือก
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              rows.map((row, index) => (
                <TableRow
                  hover
                  key={`${row.company_id}-${row.customer_code ?? index}`}
                >
                  <TableCell>{page * ROWS_PER_PAGE + index + 1}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {row.customer_name ?? "-"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.customer_code ?? "-"}
                      {row.branch_name ? ` · ${row.branch_name}` : ""}
                    </Typography>
                  </TableCell>
                  <TableCell>{row.route_code ?? "-"}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600}>
                      {numberWithCommas(row.digital_count)}
                    </Typography>
                    {row.digital_count > 0 && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        whiteSpace="nowrap"
                      >
                        <Box
                          component="span"
                          sx={{ color: TRANSFER_CHANNEL.color }}
                        >
                          โอน {numberWithCommas(row.transfer_count)}
                        </Box>
                        {" · "}
                        <Box component="span" sx={{ color: QR_CHANNEL.color }}>
                          QR {numberWithCommas(row.qr_count)}
                        </Box>
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {numberWithCommas(row.bill_count)}
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{
                        color:
                          row.digital_count > 0
                            ? DIGITAL_GROUP.color
                            : undefined,
                      }}
                    >
                      {formatNumber(row.digital_percent)}%
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {formatNumber(row.digital_amount)}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={(_, nextPage) => setPage(nextPage)}
        rowsPerPage={ROWS_PER_PAGE}
        rowsPerPageOptions={[ROWS_PER_PAGE]}
        labelDisplayedRows={({ from, to, count: total }) =>
          `${from}-${to} จาก ${total}`
        }
      />
    </Paper>
  );
}
